// src/pages/HrDashboard/UnregisteredInvitationsTile.js
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// WICHTIG: unsere vorkonfigurierte Instanz + Session-Helfer
import { mentoringApi } from '../../axiosInstance';
import { ensureMentoringSession } from '../../lib/session';

function UnregisteredInvitationsTile({ tenantId, selectedSurveyId }) {
  const { t, i18n } = useTranslation();

  const [invites, setInvites] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const [selectedIds, setSelectedIds] = useState([]);
  // Defaultwerte über t() für Reminder
  const [reminderSubject, setReminderSubject] = useState(
    t('unregInvitations.reminderSubject', 'Kleine Erinnerung an Mentoring!')
  );
  const [reminderText, setReminderText] = useState(
    t(
      'unregInvitations.reminderText',
      `Hallo,\n\nnoch ein kurzer Hinweis, sich zu registrieren.\nViele Grüße,\nDein HR-Team`
    )
  );

  // Sprachwechsel beachten (z. B. bei dynamischer Sprache im System)
  useEffect(() => {
    setReminderSubject(
      t('unregInvitations.reminderSubject', 'Kleine Erinnerung an Mentoring!')
    );
    setReminderText(
      t(
        'unregInvitations.reminderText',
        `Hallo,\n\nnoch ein kurzer Hinweis, sich zu registrieren.\nViele Grüße,\nDein HR-Team`
      )
    );
    // eslint-disable-next-line
  }, [i18n.language]);

  useEffect(() => {
    loadInvitations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, selectedSurveyId, sortBy, sortDir]);

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(i18n.language);
  };

  async function loadInvitations() {
    if (!tenantId) {
      setError(t('unregInvitations.noTenantId', 'Keine tenantId vorhanden'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Sicherstellen, dass eine Mentoring-Session existiert
      await ensureMentoringSession();

      let url = '/mentoring/invitations/all?';
      const params = [];

      // ✅ FIX: tenant_id immer mitsenden, sonst liefert das Backend oft [] zurück
      params.push(`tenant_id=${encodeURIComponent(tenantId)}`);

      if (selectedSurveyId) params.push(`survey_id=${selectedSurveyId}`);
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (sortBy) params.push(`sortBy=${encodeURIComponent(sortBy)}`);
      if (sortDir) params.push(`sortDir=${encodeURIComponent(sortDir)}`);

      if (params.length > 0) url += params.join('&');

      const res = await mentoringApi.get(url, { withCredentials: true });
      setInvites(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError(
        err?.response?.data?.error ||
          t(
            'unregInvitations.loadError',
            'Fehler beim Laden der Einladungen'
          )
      );
    } finally {
      setLoading(false);
    }
  }

  function handleCheckboxChange(invId, checked) {
    setSelectedIds((prev) =>
      checked ? [...prev, invId] : prev.filter((id) => id !== invId)
    );
  }

  async function handleSendReminder() {
    try {
      if (selectedIds.length === 0) {
        alert(
          t('unregInvitations.noSelection', 'Keine Einladungen ausgewählt!')
        );
        return;
      }

      await ensureMentoringSession();

      const body = {
        invitationIds: selectedIds,
        subject: reminderSubject,
        text: reminderText,
      };
      const res = await mentoringApi.post(
        '/mentoring/invitations/reminder',
        body,
        { withCredentials: true }
      );
      alert(
        t(
          'unregInvitations.reminderSent',
          { count: res.data?.count ?? 0 },
          `Reminder verschickt. Gesendet an ${res.data?.count ?? 0} Einladungen.`
        )
      );
      setSelectedIds([]);
    } catch (err) {
      console.error('Fehler beim Reminder:', err);
      alert(
        err?.response?.data?.error ||
          t('unregInvitations.reminderError', 'Fehler beim Reminder-Senden')
      );
    }
  }

  async function handleDeleteInvite(inviteId) {
    if (
      !window.confirm(
        t('unregInvitations.deleteConfirm', 'Einladung wirklich löschen?')
      )
    )
      return;
    try {
      await ensureMentoringSession();
      await mentoringApi.delete(`/mentoring/invitations/${inviteId}`, {
        withCredentials: true,
      });
      setInvites((prev) => prev.filter((inv) => inv.invitation_id !== inviteId));
      setSelectedIds((prev) => prev.filter((id) => id !== inviteId));
    } catch (err) {
      alert(
        t(
          'unregInvitations.deleteError',
          'Fehler beim Löschen der Einladung'
        )
      );
    }
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    loadInvitations();
  }

  if (loading)
    return (
      <p className="text-gray-400">
        {t('unregInvitations.loading', 'Lade Einladungen...')}
      </p>
    );
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="bg-gray-800 p-6 rounded-2xl shadow-md mt-8">
      <h2 className="text-xl font-bold mb-4">
        {t('unregInvitations.heading', 'Offene Einladungen')}{' '}
        {selectedSurveyId
          ? `(${t('unregInvitations.survey', 'Umfrage')} #${selectedSurveyId})`
          : `(${t('unregInvitations.allSurveys', 'alle Umfragen')})`}
      </h2>

      <form
        onSubmit={handleSearchSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6"
      >
        <div>
          <label className="block mb-1 text-sm">
            {t('unregInvitations.search', 'Suche (E-Mail):')}
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full p-2 rounded bg-gray-700"
            placeholder={t('unregInvitations.searchPlaceholder', 'E-Mail suchen')}
          />
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="block mb-1 text-sm">
              {t('unregInvitations.sortBy', 'Sortiere nach:')}
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full p-2 rounded bg-gray-700"
            >
              <option value="email">{t('unregInvitations.email', 'E-Mail')}</option>
              <option value="role">{t('unregInvitations.role', 'Rolle')}</option>
              <option value="created_at">
                {t('unregInvitations.createdAt', 'Erstellt am')}
              </option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block mb-1 text-sm">
              {t('unregInvitations.sortDir', 'Richtung:')}
            </label>
            <select
              value={sortDir}
              onChange={(e) => setSortDir(e.target.value)}
              className="w-full p-2 rounded bg-gray-700"
            >
              <option value="asc">
                {t('unregInvitations.asc', 'Aufsteigend')}
              </option>
              <option value="desc">
                {t('unregInvitations.desc', 'Absteigend')}
              </option>
            </select>
          </div>
        </div>
        <button
          type="submit"
          className="md:col-span-2 bg-mentixxgreen text-black font-bold py-2 px-4 rounded hover:bg-green-400 transition mt-4"
        >
          {t('unregInvitations.update', 'Aktualisieren')}
        </button>
      </form>

      <div className="bg-gray-700 p-4 rounded-lg mb-6">
        <h3 className="text-lg font-semibold mb-2">
          {t('unregInvitations.reminderSelected', 'Reminder an Ausgewählte')}
        </h3>
        <label className="block mb-1 text-sm">
          {t('unregInvitations.subject', 'Betreff:')}
        </label>
        <input
          type="text"
          value={reminderSubject}
          onChange={(e) => setReminderSubject(e.target.value)}
          className="w-full p-2 rounded bg-gray-600 mb-3"
        />
        <label className="block mb-1 text-sm">
          {t('unregInvitations.text', 'Text:')}
        </label>
        <textarea
          value={reminderText}
          onChange={(e) => setReminderText(e.target.value)}
          rows="4"
          className="w-full p-2 rounded bg-gray-600 mb-3"
        />
        <button
          type="button"
          onClick={handleSendReminder}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          {t('unregInvitations.sendReminder', 'Reminder senden')}
        </button>
      </div>

      {invites.length === 0 ? (
        <p className="text-gray-400">
          {t('unregInvitations.noneFound', 'Keine offenen Einladungen gefunden.')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-700 text-gray-300">
              <tr>
                <th className="p-2 text-left">
                  {t('unregInvitations.select', 'Auswahl')}
                </th>
                <th className="p-2 text-left">{t('unregInvitations.id', 'ID')}</th>
                <th className="p-2 text-left">
                  {t('unregInvitations.email', 'E-Mail')}
                </th>
                <th className="p-2 text-left">
                  {t('unregInvitations.role', 'Rolle')}
                </th>
                <th className="p-2 text-left">
                  {t('unregInvitations.createdAt', 'Erstellt am')}
                </th>
                <th className="p-2 text-left">
                  {t('unregInvitations.validUntil', 'Gültig bis')}
                </th>
                <th className="p-2 text-left">
                  {t('unregInvitations.action', 'Aktion')}
                </th>
              </tr>
            </thead>
            <tbody>
              {invites.map((inv) => {
                const isChecked = selectedIds.includes(inv.invitation_id);

                const validUntilRaw =
                  inv.invite_valid_until ??
                  inv.inviteValidUntil ??
                  inv.valid_until ??
                  inv.validUntil ??
                  inv.expires_at ??
                  inv.expiresAt ??
                  null;

                return (
                  <tr key={inv.invitation_id} className="border-t border-gray-600">
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) =>
                          handleCheckboxChange(inv.invitation_id, e.target.checked)
                        }
                      />
                    </td>
                    <td className="p-2">{inv.invitation_id}</td>
                    <td className="p-2">{inv.email}</td>
                    <td className="p-2">{inv.role}</td>
                    <td className="p-2">{formatDate(inv.created_at)}</td>
                    <td className="p-2">{formatDate(validUntilRaw)}</td>
                    <td className="p-2">
                      <button
                        onClick={() => handleDeleteInvite(inv.invitation_id)}
                        className="bg-red-600 hover:bg-red-800 text-white py-1 px-3 rounded"
                        title={t('unregInvitations.deleteTitle', 'Einladung löschen')}
                      >
                        {t('unregInvitations.delete', 'Löschen')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default UnregisteredInvitationsTile;
